'use client';

import { use, useEffect } from 'react';
import LayoutGrid, {
	ResizableGridProps,
} from 'src/components/Grids/LayoutGrid/LayoutGrid';
import NavFooterLayout from 'src/components/Layouts/NavFooterLayout';
import SwapBar from 'src/components/Swap/SwapBar';
import SwapForm from 'src/components/Swap/SwapForm';
import SwapPageTable from 'src/components/Swap/SwapPageTable';
import BREAKPOINTS from 'src/constants/breakpoints';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import useSafePush from 'src/hooks/useSafePush';
import useTokenList from 'src/hooks/swap/useTokenList';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

import dynamic from 'next/dynamic';
import { useWindowSizeIsGreaterThan } from 'src/hooks/useWindowSize';
import { MAIN_POOL_ID } from '@drift/common';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { Close } from '@drift-labs/icons';
import { useTitanBadgeData } from 'src/hooks/useTitanBadge';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';

const SwapPriceChart = dynamic(
	() => import('src/components/Swap/SwapPriceChart'),
	{ ssr: false }
);

const SwapPage = (props: { params: Promise<{ swapPair?: string[] }> }) => {
	const params = use(props.params);

	// Use useSyncLocalStorage for badge dismissal states
	const [badgeDismissed, setBadgeDismissed] = useSyncLocalStorage(
		'titanBadgeDismissed',
		false
	);
	const [earnedBadgeDismissed, setEarnedBadgeDismissed] = useSyncLocalStorage(
		'titanEarnedBadgeDismissed',
		false
	);

	// Handler functions for dismissing banners
	const handleDismissBadge = () => {
		setBadgeDismissed(true);
	};

	const handleDismissEarnedBadge = () => {
		setEarnedBadgeDismissed(true);
	};

	// Check badge status and count to determine if banner should show
	const { hasBadge, count, isLoading } = useTitanBadgeData();

	// Determine if we should show the badge banner
	// Show if: user hasn't earned badge AND total badges < 10k AND user hasn't manually dismissed it
	const shouldShowBadgeBanner =
		!badgeDismissed &&
		!isLoading &&
		!hasBadge.data?.has_badge &&
		(count.data?.badge_count ?? 0) < 10000;

	// Determine if we should show the "Badge Earned" banner
	// Show if: user HAS earned badge AND user hasn't manually dismissed the earned badge banner
	const shouldShowEarnedBadgeBanner =
		!earnedBadgeDismissed && !isLoading && hasBadge.data?.has_badge === true;

	const setStore = useDriftStore((s) => s.set);
	const tokenList = useTokenList();
	const safePush = useSafePush();
	const isTabletScreenSize = useWindowSizeIsGreaterThan('xs');
	const poolId = useDriftAccountStore(
		(s) =>
			s.accounts[s.currentUserKey ?? '']?.client?.getUserAccount()?.poolId ||
			MAIN_POOL_ID
	);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();

	useEffect(() => {
		extractAndSetTokenMintsFromPath(params.swapPair?.[0]);
	}, [params.swapPair?.[0], tokenList]);

	const setDefaultSwapParams = () => {
		setStore((s) => {
			if (0 !== s.swap.fromMarketIndex || 1 !== s.swap.toMarketIndex) {
				s.swap = {
					fromMarketIndex: 0,
					toMarketIndex: 1,
					poolId: MAIN_POOL_ID,
					swapDetails: s.swap.swapDetails, // need to preserve if there is a swap modal
				};
			}
		});
	};

	const extractAndSetTokenMintsFromPath = (query: string | undefined) => {
		if (!query) {
			setDefaultSwapParams();
			return;
		}

		let fromTokenSymbol: string;
		let toTokenSymbol: string;

		if (poolId === MAIN_POOL_ID) {
			[fromTokenSymbol, toTokenSymbol] = [
				query.split('-')[0],
				query.split('-')[1],
			];
		} else {
			const match = query.match(/^(.*?-\d+)(-.*)$/);
			if (!match) {
				[fromTokenSymbol, toTokenSymbol] = [
					query.split('-')[0],
					query.split('-')[1],
				];
			} else {
				[fromTokenSymbol, toTokenSymbol] = [match[1], match[2].slice(1)];
				// Trim off any trailing -number
				toTokenSymbol = toTokenSymbol.replace(/-\d+$/, '');
			}
		}

		if (!fromTokenSymbol || !toTokenSymbol) {
			setDefaultSwapParams();
			safePush('/swap');
			return;
		}

		const markets = spotMarketsSortedByMarketIndex.filter(
			(market) => market.poolId === poolId
		);

		const fromMarketIndex = markets.find(
			(market) => market.symbol === fromTokenSymbol
		)?.marketIndex;
		const toMarketIndex = markets.find(
			(market) => market.symbol === toTokenSymbol
		)?.marketIndex;

		if (fromMarketIndex === undefined || toMarketIndex === undefined) {
			setDefaultSwapParams();
			safePush('/swap');
			return;
		}

		setStore((s) => {
			if (
				fromMarketIndex !== s.swap.fromMarketIndex ||
				toMarketIndex !== s.swap.toMarketIndex
			) {
				s.swap = {
					fromMarketIndex,
					toMarketIndex,
					poolId,
					swapDetails: s.swap.swapDetails, // need to preserve if there is a swap modal
				};
			}
		});
	};

	const cells: ResizableGridProps['cells'] = [
		{
			Element: <SwapBar />,
			cellBreakpoints: [
				// SwapBar matches chart width, positioned above it
				[BREAKPOINTS.SMALL, 1, 1, 1, 1], // Row 1, spans chart width
			],
			skipWrapper: true,
		},
		{
			Element: (
				<div className="flex h-full">
					<SwapPriceChart />
				</div>
			),
			cellBreakpoints: [[BREAKPOINTS.SMALL, 1, 1, 2, 1]], // Row 2, chart width
			renderCondition: () => isTabletScreenSize,
		},
		{
			Element: <SwapPageTable />,
			cellBreakpoints: [
				// Adjust for SwapBar being present
				[BREAKPOINTS.XSMALL, 1, 1, 2, 1],
				[BREAKPOINTS.SMALL, 1, 1, 3, 1],
			],
			skipWrapper: true,
		},
		{
			Element: <SwapForm />,
			cellBreakpoints: [
				// Starts from row 1, but needs to account for SwapBar
				[BREAKPOINTS.XSMALL, 1, 1, 1, 1],
				[BREAKPOINTS.SMALL, 2, 1, 1, 3], // Column 2, starts row 1, spans 3 rows
				[BREAKPOINTS.LARGE, 2, 1, 1, 3],
			],
			skipWrapper: true,
		},
	];

	return (
		<NavFooterLayout overflowAuto hideScroll>
			{shouldShowBadgeBanner && (
				<div className="mb-1 flex items-center justify-between bg-gradient-to-r from-[#6C1D73] via-[#5819DC] to-[#6954EE] px-4 py-3 text-text-default max-sm:mx-0 sm:mx-lg sm:rounded-sm">
					<span className="flex items-center gap-xs max-sm:gap-md">
						<img
							src="/assets/images/swap/titan-badge.webp"
							alt="Titan Drift Badge"
							className="h-[17px] w-[14.5px]"
						/>
						<span className="typo-b4 text-white">
							Titan&apos;s Drift Badge available to the first 10k users who
							execute a minimum of $100 in Drift Swap, powered by Titan.
						</span>
					</span>
					<button
						type="button"
						aria-label="Dismiss badge"
						onClick={handleDismissBadge}
						className="ml-2 inline-block h-4 w-4 cursor-pointer"
					>
						<Close className="h-4 w-4" color="white" />
					</button>
				</div>
			)}
			{shouldShowEarnedBadgeBanner && (
				<div className="light:bg-[rgba(106,45,112,0.5)] mx-1 mb-sm flex items-center justify-between bg-[rgba(108,29,115,0.5)] px-4 py-3 typo-b4 text-text-default max-sm:mx-0 sm:mx-lg sm:rounded-sm">
					<span className="flex items-center gap-xs max-sm:gap-md">
						<img
							src="/assets/images/swap/titan-badge.webp"
							alt="Titan Badge Earned"
							className="h-[17px] w-[14.5px]"
						/>
						<span>Titan Badge Earned</span>
					</span>
					<button
						type="button"
						aria-label="Dismiss earned badge banner"
						onClick={handleDismissEarnedBadge}
						className="ml-2 inline-block h-4 w-4 cursor-pointer"
					>
						<Close className="h-4 w-4" color="white" />
					</button>
				</div>
			)}
			<LayoutGrid
				breakpointsStyleDefinitions={[
					{
						breakPoint: BREAKPOINTS.XSMALL,
						rowDefs: 'auto auto',
						columnDefs: '100%',
					},
					{
						breakPoint: BREAKPOINTS.SMALL,
						rowDefs: '68px 376px minmax(212px, auto)', // SwapBar + Chart + Table
						// first column is calculated precisely to prevent the min-width of the swap table from causing the entire grid to overflow
						columnDefs: 'calc(100% - 308px) 300px',
					},
					{
						breakPoint: BREAKPOINTS.XLARGE,
						rowDefs: '68px 376px minmax(212px, auto)', // SwapBar + Chart + Table
						// first column is calculated precisely to prevent the min-width of the swap table from causing the entire grid to overflow
						columnDefs: 'calc(100% - 308px) 300px',
					},
				]}
				cells={cells}
				className="h-full gap-xs sm:min-h-[950px] sm:gap-sm sm:px-lg sm:pb-sm"
			/>
		</NavFooterLayout>
	);
};

export default SwapPage;
